/**
 * Purpose: Orchestration layer for the Business Detail page — fetching, derived
 *          state, owner detection, module availability, and owner mutations.
 * Inputs: slugOrId (URL segment).
 * Outputs: { business, loading, error, products, menuItems, services, reviews,
 *            relatedBusinesses, views, canManage, modules, moduleCtx,
 *            refreshCatalogue, refreshReviews, deleteProduct, deleteService,
 *            submitReview, reload }.
 * Notes: products/services are seeded from the detail response's embedded
 *        arrays (decision D-10) — no duplicate initial fetches; targeted
 *        refetches happen only after owner mutations.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { businessService } from '../services/api';
import { normalizeCatalogueList } from '../services/businessService';
import inventoryService from '../services/inventoryService';
import { canManageOwnedResource } from '../utils/resourcePermissions';
import { resolveModules } from '../config/businessModules';
import { getBusinessMedia } from '../utils/media';
import { getBusinessContacts } from '../utils/contacts';

function useBusinessDetail(slugOrId) {
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(!!slugOrId);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [relatedBusinesses, setRelatedBusinesses] = useState([]);
  const [views, setViews] = useState(0);
  const viewCountedFor = useRef(null);

  const reload = useCallback(async () => {
    if (!slugOrId) return;
    try {
      setError(null);
      const data = await businessService.getBusiness(slugOrId);
      setBusiness(data);
      setViews(data.views || 0);
      setProducts(normalizeCatalogueList(data.products));
      setServices(normalizeCatalogueList(data.services));
    } catch (err) {
      console.error('Error loading business:', err);
      setError('Failed to load business details');
      setBusiness(null);
    } finally {
      setLoading(false);
    }
  }, [slugOrId]);

  const refreshReviews = useCallback(async () => {
    if (!slugOrId) return;
    try {
      const response = await businessService.getBusinessReviews(slugOrId);
      setReviews(response.results || response || []);
    } catch (err) {
      console.error('Error loading business reviews:', err);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  }, [slugOrId]);

  const businessId = business?.id;

  const refreshCatalogue = useCallback(async () => {
    if (!businessId) return;
    try {
      const [productsResponse, servicesResponse] = await Promise.all([
        businessService.getProducts(businessId),
        businessService.getServices(businessId),
      ]);
      setProducts(normalizeCatalogueList(productsResponse));
      setServices(normalizeCatalogueList(servicesResponse));
    } catch (err) {
      console.error('Error refreshing catalogue:', err);
    }
  }, [businessId]);

  // Initial load: detail (with embedded catalogue) + reviews in parallel.
  useEffect(() => {
    if (!slugOrId) return;
    setLoading(true);
    setReviewsLoading(true);
    reload();
    refreshReviews();
  }, [slugOrId, reload, refreshReviews]);

  // Once per identifier: count the view and pull related businesses.
  useEffect(() => {
    if (!business?.id || viewCountedFor.current === business.id) return;
    viewCountedFor.current = business.id;

    businessService.incrementViews(business.id).catch((err) => {
      console.error('Error incrementing views:', err);
    });

    businessService
      .getBusinesses({ category: business.category, page_size: 6, exclude: business.id })
      .then((response) => {
        const list = (response.results || response || []).filter(
          (b) => b.id !== business.id && b.slug !== business.slug,
        );
        setRelatedBusinesses(list);
      })
      .catch((err) => {
        console.error('Error loading related businesses:', err);
        setRelatedBusinesses([]);
      });
  }, [business]);

  // Mutations route through inventoryService — the single catalogue mutation
  // path (ADR-007). Optimistic UI + rollback stay here (UI concern); the service
  // owns the network operation.
  const deleteProduct = useCallback(
    async (productId) => {
      await inventoryService.deleteProduct(businessId, productId);
      setProducts((prev) => prev.filter((p) => p.id !== productId));
    },
    [businessId],
  );

  const deleteService = useCallback(
    async (serviceId) => {
      await inventoryService.deleteService(businessId, serviceId);
      setServices((prev) => prev.filter((s) => s.id !== serviceId));
    },
    [businessId],
  );

  const toggleProductAvailability = useCallback(
    async (product) => {
      const next = !(product.is_available !== false);
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, is_available: next } : p)));
      try {
        await inventoryService.setProductAvailability(businessId, product.id, next);
      } catch (err) {
        // Revert the optimistic update on failure.
        setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, is_available: !next } : p)));
        throw err;
      }
    },
    [businessId],
  );

  const toggleServiceAvailability = useCallback(
    async (service) => {
      const next = !(service.is_available !== false);
      setServices((prev) => prev.map((s) => (s.id === service.id ? { ...s, is_available: next } : s)));
      try {
        await inventoryService.setServiceAvailability(businessId, service.id, next);
      } catch (err) {
        setServices((prev) => prev.map((s) => (s.id === service.id ? { ...s, is_available: !next } : s)));
        throw err;
      }
    },
    [businessId],
  );

  const submitReview = useCallback(
    async (reviewForm) => {
      await businessService.createBusinessReview(slugOrId, reviewForm);
      await Promise.all([refreshReviews(), reload()]);
    },
    [slugOrId, refreshReviews, reload],
  );

  const canManage = !!(user && business && canManageOwnedResource(user, business));
  const menuItems = useMemo(() => products.filter((p) => p.is_menu_item), [products]);
  const displayProducts = useMemo(() => products.filter((p) => !p.is_menu_item), [products]);

  const moduleCtx = useMemo(
    () => ({
      business,
      isOwner: canManage,
      data: {
        products: displayProducts,
        menuItems,
        services,
        galleryImages: business?.images || [],
        galleryMedia: getBusinessMedia(business),
        teamMembers: Array.isArray(business?.team_members) ? business.team_members : [],
        contacts: getBusinessContacts(business),
        bridges: business?.bridges || {},
        reviews,
      },
    }),
    [business, canManage, displayProducts, menuItems, services, reviews],
  );

  const modules = useMemo(() => resolveModules(moduleCtx), [moduleCtx]);

  return {
    business,
    loading,
    error,
    products: displayProducts,
    menuItems,
    services,
    reviews,
    reviewsLoading,
    relatedBusinesses,
    views,
    canManage,
    modules,
    moduleCtx,
    reload,
    refreshCatalogue,
    refreshReviews,
    deleteProduct,
    deleteService,
    toggleProductAvailability,
    toggleServiceAvailability,
    submitReview,
  };
}

export default useBusinessDetail;
